import { useState, useEffect } from "react";
import {
  Drawer, Box, Typography, Button, TextField,
  ToggleButtonGroup, ToggleButton, Chip, Divider, Alert,
  Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import { DateRange } from "react-date-range";
import { fr } from "date-fns/locale";
import axios from "axios";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { eurToKmf, formatKmf } from "./utils/currency"; // 🆕 adapter le chemin si besoin

const API = "https://api.komorotel.com/api";

/* 🆕 PÉRIODES — unité de vente d'un tarif.
   Sur un plan weekly ou monthly, le prix saisi couvre la période entière.
   Le montrer partout évite qu'un hébergeur vende son mois au prix d'une nuit. */
const UNIT = {
  short_stay: { label: "nuit",    nights: 1  },
  weekly:     { label: "semaine", nights: 7  },
  monthly:    { label: "mois",    nights: 30 },
};

const normalizeStayType = (t) =>
  t === "long_stay" ? "monthly" : (["short_stay", "weekly", "monthly"].includes(t) ? t : "short_stay");

const unitOf = (stayType) => UNIT[normalizeStayType(stayType)];
const isPeriodPlan = (stayType) => normalizeStayType(stayType) !== "short_stay";

const BulkEditDrawer = ({ open, onClose, rooms, onApply }) => {
  const [step, setStep] = useState(1);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [dateRange, setDateRange] = useState([{
    startDate: new Date(),
    endDate: new Date(),
    key: "selection"
  }]);
  const [editType, setEditType] = useState("price");
  const [value, setValue] = useState("");
  const [selectedRatePlan, setSelectedRatePlan] = useState("");
  const [ratePlans, setRatePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loading, setLoading] = useState(false);

  // Charger les rate plans de la première room sélectionnée
  useEffect(() => {
    if (editType !== "price" || selectedRooms.length === 0) {
      setRatePlans([]);
      return;
    }

    setLoadingPlans(true);
    axios.get(`${API}/rateprices/room/${selectedRooms[0]}`, { withCredentials: true })
      .then(res => {
        const uniquePlans = [];
        const seen = new Set();
        res.data.forEach(rp => {
          const planId = rp.ratePlanId?._id || rp.ratePlanId;
          if (!seen.has(planId?.toString())) {
            seen.add(planId?.toString());
            uniquePlans.push({
              _id: planId,
              name: rp.ratePlanId?.name || "Tarif",
              // 🆕 PÉRIODES — le backend renseigne stayType dans le populate
              stayType: normalizeStayType(rp.ratePlanId?.stayType),
            });
          }
        });
        setRatePlans(uniquePlans);
        if (uniquePlans.length === 1) setSelectedRatePlan(uniquePlans[0]._id);
      })
      .catch(console.error)
      .finally(() => setLoadingPlans(false));
  }, [selectedRooms, editType]);

  const toggleRoom = (roomId) => {
    setSelectedRooms(prev =>
      prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]
    );
  };

  const selectAll = () => {
    setSelectedRooms(rooms.map(r => r.roomId || r._id));
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      await onApply({
        roomIds: selectedRooms,
        startDate: dateRange[0].startDate,
        endDate: dateRange[0].endDate,
        type: editType,
        value: editType === "status" ? value : Number(value),
        ratePlanId: editType === "price" ? selectedRatePlan : null
      });
      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedRooms([]);
    setValue("");
    setSelectedRatePlan("");
    setRatePlans([]);
    onClose();
  };

  const daysCount = Math.ceil(
    (dateRange[0].endDate - dateRange[0].startDate) / (1000 * 60 * 60 * 24)
  ) + 1;

  const canProceed = {
    1: selectedRooms.length > 0,
    2: true,
    3: value !== "" && (editType !== "price" || selectedRatePlan !== "")
  };

  // 🆕 PÉRIODES — tarif ciblé et son unité
  const currentPlan = ratePlans.find(p => p._id?.toString() === selectedRatePlan?.toString());
  const unit = currentPlan ? unitOf(currentPlan.stayType) : UNIT.short_stay;
  const period = currentPlan ? isPeriodPlan(currentPlan.stayType) : false;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: 450, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

        {/* HEADER */}
        <Box sx={{ p: 3, borderBottom: "1px solid #E2E8F0" }}>
          <Typography variant="h6" fontWeight={600}>Modification groupée</Typography>
          <Typography variant="body2" color="text.secondary">
            Étape {step}/3
          </Typography>

          {/* Progress */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            {[1, 2, 3].map(s => (
              <Box
                key={s}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: s <= step ? "var(--k-green)" : "#E2E8F0"
                }}
              />
            ))}
          </Box>
        </Box>

        {/* CONTENT */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>

          {/* STEP 1: ROOMS */}
          {step === 1 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography fontWeight={600}>Sélectionner les chambres</Typography>
                <Button size="small" onClick={selectAll}>Tout</Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {rooms.map(room => {
                  const id = room.roomId || room._id;
                  const isSelected = selectedRooms.includes(id);

                  return (
                    <Box
                      key={id}
                      onClick={() => toggleRoom(id)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: isSelected ? "2px solid var(--k-green)" : "1px solid #E2E8F0",
                        backgroundColor: isSelected ? "var(--k-green-soft)" : "white",
                        cursor: "pointer",
                        transition: "all 0.15s"
                      }}
                    >
                      <Typography fontWeight={isSelected ? 600 : 400}>
                        {room.title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {selectedRooms.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {selectedRooms.length} chambre(s) sélectionnée(s)
                </Alert>
              )}
            </>
          )}

          {/* STEP 2: DATES */}
          {step === 2 && (
            <>
              <Typography fontWeight={600} mb={2}>Période</Typography>

              <DateRange
                ranges={dateRange}
                onChange={item => setDateRange([item.selection])}
                locale={fr}
                months={1}
                direction="vertical"
                rangeColors={["var(--k-green)"]}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                {daysCount} jour(s) sélectionné(s)
              </Alert>
            </>
          )}

          {/* STEP 3: VALUE */}
          {step === 3 && (
            <>
              <Typography fontWeight={600} mb={2}>Que modifier ?</Typography>

              <ToggleButtonGroup
                value={editType}
                exclusive
                onChange={(e, val) => val && setEditType(val)}
                fullWidth
                sx={{ mb: 3 }}
              >
                <ToggleButton value="price">Prix</ToggleButton>
                <ToggleButton value="inventory">Stock</ToggleButton>
                <ToggleButton value="status">Statut</ToggleButton>
              </ToggleButtonGroup>

              {/* Sélection rate plan si prix */}
              {editType === "price" && (
                <>
                  {loadingPlans ? (
                    <Typography variant="body2" color="text.secondary">
                      Chargement des tarifs...
                    </Typography>
                  ) : ratePlans.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Aucun tarif configuré pour cette chambre
                    </Alert>
                  ) : (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Tarif</InputLabel>
                      <Select
                        value={selectedRatePlan}
                        label="Tarif"
                        onChange={(e) => setSelectedRatePlan(e.target.value)}
                      >
                        {ratePlans.map(plan => (
                          <MenuItem key={plan._id} value={plan._id}>
                            {/* 🆕 PÉRIODES — l'unité dès la sélection */}
                            {plan.name} — / {unitOf(plan.stayType).label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}

              {/* 🆕 PÉRIODES — avertissement d'unité avant la saisie */}
              {editType === "price" && period && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Ce tarif se vend {unit.label === "mois" ? "au mois" : "à la semaine"} :
                  saisissez le prix de {unit.nights} nuits, pas un prix par nuit.
                </Alert>
              )}

              {/* Valeur */}
              {editType === "status" ? (
                <ToggleButtonGroup
                  value={value}
                  exclusive
                  onChange={(e, val) => val && setValue(val)}
                  fullWidth
                >
                  <ToggleButton value="opened">Ouvert</ToggleButton>
                  <ToggleButton value="closed">Fermé</ToggleButton>
                </ToggleButtonGroup>
              ) : (
                <TextField
                  fullWidth
                  type="number"
                  label={
                    editType === "price"
                      // 🆕 PÉRIODES — le libellé porte l'unité
                      ? `Prix (€ / ${unit.label})`
                      : "Nombre de chambres"
                  }
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  InputProps={{
                    endAdornment: editType === "price"
                      ? <Typography color="text.secondary">€ / {unit.label}</Typography>
                      : null
                  }}
                />
              )}

              {/* 🆕 DEVISES — équivalent KMF, calculé côté front pour l'aperçu */}
              {editType === "price" && value > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: "#64748B" }}>
                  soit {formatKmf(eurToKmf(value))} KMF / {unit.label}
                </Typography>
              )}

              {/* 🆕 PÉRIODES — équivalent par nuit, indicatif.
                  Remplace l'ancienne estimation « × 30 », qui multipliait un
                  prix déjà mensuel. */}
              {editType === "price" && period && value > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5, color: "var(--k-green)", fontWeight: 600 }}>
                  ≈ {Math.round(Number(value) / unit.nights)} € / nuit pour le client
                </Typography>
              )}

              {/* Récap */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: "#F8FAFC", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Récapitulatif
                </Typography>
                <Typography variant="body2">
                  • {selectedRooms.length} chambre(s)
                </Typography>
                <Typography variant="body2">
                  • {daysCount} jour(s)
                </Typography>
                {editType === "price" && selectedRatePlan && (
                  <Typography variant="body2">
                    • Tarif : {currentPlan?.name}
                  </Typography>
                )}
                <Typography variant="body2">
                  • {editType === "price" && `Prix : ${value || 0} € / ${unit.label}`}
                  {editType === "inventory" && `Stock : ${value || 0}`}
                  {editType === "status" && `Statut : ${value === "opened" ? "Ouvert" : "Fermé"}`}
                </Typography>
              </Box>
            </>
          )}

        </Box>

        {/* FOOTER */}
        <Box sx={{ p: 3, borderTop: "1px solid #E2E8F0", display: "flex", gap: 2 }}>
          {step > 1 && (
            <Button
              variant="outlined"
              onClick={() => setStep(step - 1)}
              sx={{ flex: 1 }}
            >
              Retour
            </Button>
          )}

          {step < 3 ? (
            <Button
              variant="contained"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed[step]}
              sx={{ flex: 1 }}
            >
              Suivant
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!canProceed[3] || loading}
              sx={{ flex: 1 }}
            >
              {loading ? "Application..." : "Appliquer"}
            </Button>
          )}
        </Box>

      </Box>
    </Drawer>
  );
};

export default BulkEditDrawer;